import { cloneDeep, debounce, isEmpty } from 'lodash';
import { useEffect, useMemo, useState } from 'react';
import { Col, Row } from 'reactstrap';
import { getGLTemplateId } from '../../../api/glTemplate';
import {
  getGLConfigData,
  getGLConfigEvents,
  updateGLConfigData,
} from '../../../api/journalGLConfig';
import EmptyEvent from '../../../assets/images/j-gl.svg';
import { Button, InfoLabel, Layout, Select, Table } from '../../../common/components';
import { useSetLoading } from '../../../common/hooks/appearance';
import { apiErrorHandler } from '../../../utils/handlers';
import { asyncDebounce } from '../../../utils/utils';
import ConfirmPopup from './ConfirmPopup';
import SuccessPopup from './SuccessPopup';

const JournalGlconfig = () => {
  const [loading, setLoading] = useState(false);
  const [templateDropdownOptions, setTemplateDropdownOptions] = useState([]);
  const [eventDropdownOptions, setEventDropdownOptions] = useState([]);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [tempSelectedEvent, setTempSelectedEvent] = useState(null); // used for temporarly saving event selected, used after confirm popup actions
  const [tableData, setTableData] = useState({});
  const [showSaveConfirmPopup, setShowSaveConfirmPopup] = useState(false);
  const [showSaveSuccessPopup, setShowSaveSuccessPopup] = useState(false);
  const [showEventChangeConfirmPopup, setShowEventChangeConfirmPopup] = useState(false);
  const [changedData, setChangedData] = useState({}); // used for saving template values from onChange handler

  const [tableLeftScopllPos, setTableLeftScopllPos] = useState(0);

  const toggleSaveConfirmPopup = () => setShowSaveConfirmPopup((s) => !s);
  const toggleEventChangeConfirmPopup = () => setShowEventChangeConfirmPopup((s) => !s);
  const toggleSaveSuccessPopup = () => setShowSaveSuccessPopup((s) => !s);

  const fetchEventList = async () => {
    setLoading(true);
    try {
      const response = await getGLConfigEvents({ isTrust: true });
      setLoading(false);
      const formatedResp = response?.data?.data?.map((d) => ({
        value: d.id,
        label: d.name,
      }));
      setEventDropdownOptions(formatedResp);
      return formatedResp;
    } catch (err) {
      setLoading(false);
      apiErrorHandler(err);
    }
  };

  const fetchGLConfiguration = async (eventId) => {
    setLoading(true);
    try {
      const response = await getGLConfigData({
        glEventId: eventId,
        isTrust: true,
      });
      setLoading(false);
      const d = cloneDeep(response?.data?.data);
      // mutate the mappings array to match the locations
      // this is done to help rendering of dropdowns
      d.data.forEach((et) => {
        d.headers.forEach((h) => {
          if (et.mapping.find((m) => m.locationId === h.locationId)) return;
          et.mapping.push({
            locationId: h.locationId,
            locationName: h.locationName,
            isActive: 1,
          });
        });
      });
      setTableData(d);

      // this logic is for adding the GL Templates from GLCongig fetched in above API call to templateDropdownOptions
      // this is requied because initally we are only fetching 15 GL Templates for dropdowns
      // and the template from above API might not be available in that 15 items
      const tempObj = {};
      d.data.forEach((et) => {
        et.mapping.forEach((m) => {
          if (m.templateId && m.templateIdentificationNumber) {
            tempObj[m.templateId] = {
              value: m.templateId,
              label: m.templateIdentificationNumber,
            };
          }
        });
      });

      // for removing duplicate options
      templateDropdownOptions.forEach((to) => {
        if (to.value && to.label) tempObj[to.value] = to;
      });
      const templateDropdownOptionsFromResp = Object.values(tempObj);
      setTemplateDropdownOptions(templateDropdownOptionsFromResp);
    } catch (err) {
      setLoading(false);
      apiErrorHandler(err);
    }
  };

  const fetchGLTemplateList = async (searchVal, action = 'default') => {
    setLoading(true);
    try {
      const params = {
        searchValue: searchVal || '',
        page: 1,
        limit: 15,
      };
      const response = await getGLTemplateId(params);
      setLoading(false);
      const formatedResp = response?.data?.data?.map((d) => ({
        value: d.templateId,
        label: d.identificationNumber,
      }));

      // not saving the search results to the templateDropdownOptions
      if (action === 'default') setTemplateDropdownOptions(formatedResp);

      return formatedResp; // this is required for search
    } catch (err) {
      setLoading(false);
      apiErrorHandler(err);
    }
  };

  useEffect(() => {
    fetchEventList();
    fetchGLTemplateList('', 'default');
  }, []);

  const handleGLTemplateChange = (glTemplate, eventTypeId, locationId, ogDropdownValue) => {
    const temp = cloneDeep(changedData);
    const dataKey = `${eventTypeId}-${locationId}`;

    // if template already selected / saved don't do anythig
    // if template is same as original and it is present in changedData obj remove it
    if (ogDropdownValue.value === glTemplate.value) {
      delete temp[dataKey];
      setChangedData(temp);
    } else {
      // if original tempalte and selected template are different then add it to changedData obj
      // save the changed template to changedData for later use when calling API
      temp[dataKey] = {
        GLEventTypeId: eventTypeId,
        templateId: glTemplate.value,
        locationId,
        templateData: glTemplate, // used for displaying selected value
      };
      setChangedData(temp);

      // when a new template is selected from after searching we add it to the templateDropdownOptions for display purpose
      const tempObj = {};
      tempObj[glTemplate.value] = glTemplate;

      // for removing duplicate options
      templateDropdownOptions.forEach((to) => {
        if (to.value && to.label) tempObj[to.value] = to;
      });
      const templateDropdownOptionsFromResp = Object.values(tempObj);
      setTemplateDropdownOptions(templateDropdownOptionsFromResp);
    }
  };

  const toggleConfirmPopup = () => setShowSaveConfirmPopup((s) => !s);

  const handleSaveGLConfigs = () => {
    toggleConfirmPopup();
  };

  const handleSave = async () => {
    setLoading(true);
    try {
      // remove the templateData field which we added for render purpose from request data
      const changeArray = Object.values(changedData).map((d) => {
        const temp = d;
        delete temp?.templateData;
        return temp;
      });
      await updateGLConfigData({ data: changeArray });
      toggleSaveSuccessPopup();
      fetchGLConfiguration(selectedEvent?.value);
      toggleConfirmPopup();
      setChangedData({});
      setLoading(false);
    } catch (err) {
      setLoading(false);
      apiErrorHandler(err);
    }
  };

  const debounceApiCall = asyncDebounce(fetchGLTemplateList, 1000);

  const handleEventChange = (event) => {
    if (!isEmpty(changedData)) {
      setTempSelectedEvent(event);
      toggleEventChangeConfirmPopup();
    } else {
      setSelectedEvent(event);
      fetchGLConfiguration(event?.value);
    }
  };
  const handleCancelGLConfigs = () => setChangedData({});
  const handleEventChangeCancel = () => {
    setTempSelectedEvent(null);
    toggleEventChangeConfirmPopup();
  };
  const handleEventChangeConfirm = () => {
    setSelectedEvent(tempSelectedEvent);
    fetchGLConfiguration(tempSelectedEvent?.value);
    toggleEventChangeConfirmPopup();
    // reset states
    setChangedData({});
    setTempSelectedEvent(null);
  };

  const getDropdownValue = (eventTypeId, locationId) => {
    // using the value if present in changedData variable locally
    const dataKey = `${eventTypeId}-${locationId}`;
    const changeExists = changedData[dataKey];
    if (changeExists) {
      return {
        label: changeExists.templateData?.label,
        value: changeExists.templateId,
      };
    }
    return null;
  };

  const tableScrollHandler = () =>
    setTableLeftScopllPos(document.getElementById('gl-config-table')?.scrollLeft || 0);

  const debouncedTableScrollHandler = useMemo(
    () => debounce(tableScrollHandler, 300),
    [tableScrollHandler],
  );

  // Stop the invocation of the debounced function
  // after unmounting
  useEffect(() => {
    return () => debouncedTableScrollHandler.cancel();
  }, [debouncedTableScrollHandler.cancel]);

  const tableHeaders = tableData?.headers?.map((item) => item?.locationName) || [];

  const showLoading = useMemo(() => loading, [loading]);
  useSetLoading(showLoading);

  return (
    <Layout>
      <div className="layout-in-div">
        <div className="j-gl-heading mt-5">
          <InfoLabel showIcon label="Trust GL Configuration Screen" />
        </div>
        <hr />
        <div className="j-gl-section mt-5">
          <div className="rowdiv">
            <Row>
              <Col className="col-6">
                <h6 className="j-gl-h6">EVENTS</h6>
                <div className="j-gl-dropdown">
                  <Select
                    classNamePrefix="select-dropdown"
                    size="lg"
                    options={eventDropdownOptions}
                    value={selectedEvent}
                    onChange={handleEventChange}
                    placeholder="Select Event"
                    name="select_event"
                  />
                </div>
              </Col>
            </Row>
          </div>

          <div className="text-center">
            {selectedEvent && tableData?.data?.length > 0 ? (
              <>
                <Row>
                  <Col>
                    <div className="segment-btns mb-4">
                      <Button
                        color="default"
                        className="j-btn-1 j-btn-2"
                        size="xs1"
                        type="button"
                        onClick={handleCancelGLConfigs}
                      >
                        Cancel
                      </Button>
                      <Button
                        color="primary"
                        className="j-btn-1"
                        size="xs1"
                        type="button"
                        onClick={handleSaveGLConfigs}
                        disabled={isEmpty(changedData)}
                      >
                        Save
                      </Button>
                    </div>
                  </Col>
                </Row>
                <Table
                  headers={['contract type', ...tableHeaders]}
                  bordered
                  responsive
                  className="dashboard-table j-gl-table journal-gl-table"
                  id="gl-config-table"
                  onScroll={debouncedTableScrollHandler}
                >
                  <tbody>
                    {tableData?.data?.map((eventType) => (
                      <tr key={eventType?.GLEventTypeId} className="table-body-tr gl-j-tr">
                        <td className="text-center vertical-middle j-gl-main-td">
                          {eventType?.GLEventTypeName}
                        </td>
                        {tableData?.headers.map((locationItem, idx) => (
                          <td className="text-center" key={locationItem?.locationId}>
                            {eventType.mapping.map((mappingItem) => {
                              const originalDropdownValue =
                                mappingItem?.locationId === locationItem?.locationId
                                  ? {
                                      label: mappingItem?.templateIdentificationNumber,
                                      value: mappingItem?.templateId,
                                    }
                                  : null;
                              const changedDropdownValue = getDropdownValue(
                                eventType?.GLEventTypeId,
                                mappingItem?.locationId,
                              );

                              // if column is scrolled and some part is hidden behind fixed column
                              // disable the select dropdown, which will break ui
                              let disableSelect = false;
                              // each column has 300 fixed width
                              // if > 10% of column is scrolled left then disable the column
                              if (tableLeftScopllPos % 300 > 40) {
                                disableSelect = Math.floor(tableLeftScopllPos / 300) === idx;
                              }

                              return (
                                mappingItem?.locationId === locationItem?.locationId && (
                                  <div
                                    className="common-local-search w-100"
                                    key={`${eventType.GLEventTypeId}-${mappingItem?.locationId}`}
                                  >
                                    <i className="fas fa-search" />
                                    <Select
                                      name={`${eventType.GLEventTypeId}-${mappingItem?.locationId}`}
                                      size="lg"
                                      placeholder=""
                                      value={changedDropdownValue || originalDropdownValue}
                                      cacheOptions
                                      isAsync
                                      loadOptions={debounceApiCall}
                                      defaultOptions={templateDropdownOptions}
                                      disabled={disableSelect || !mappingItem?.isActive}
                                      onChange={(template) =>
                                        handleGLTemplateChange(
                                          template,
                                          eventType?.GLEventTypeId,
                                          mappingItem?.locationId,
                                          originalDropdownValue,
                                        )
                                      }
                                      menuPortalTarget={document.getElementById(
                                        'select-dropdown-portal',
                                      )}
                                      closeMenuOnScroll={(e) =>
                                        e?.target?.classList?.contains('j-gl-table')
                                      }
                                    />
                                  </div>
                                )
                              );
                            })}
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </>
            ) : (
              <img src={EmptyEvent} alt="icon" className="j-gl-img-no-events" />
            )}
          </div>
        </div>
        <ConfirmPopup
          isOpen={showSaveConfirmPopup}
          toggle={toggleSaveConfirmPopup}
          onConfirm={handleSave}
          message="Do you really want to save the changes ?"
          confirmBtnText="CONFIRM"
          cancelBtnText="CANCEL"
        />
        <ConfirmPopup
          isOpen={showEventChangeConfirmPopup}
          toggle={handleEventChangeCancel}
          onConfirm={handleEventChangeConfirm}
          message="Do you want to discard the changes?"
          confirmBtnText="Yes"
          cancelBtnText="No"
        />
        <SuccessPopup isOpen={showSaveSuccessPopup} toggle={toggleSaveSuccessPopup} />
        <div id="select-dropdown-portal" className="select-dropdown-portal" />
      </div>
    </Layout>
  );
};

export default JournalGlconfig;
